import React from 'react'
import ReactDOM from 'react-dom/client'
import { App } from './App'
import './index.css'
import { initializeOfflineSupport } from '@/lib/offline'
import { ThemeProvider } from '@/components/ui/theme-provider'
import { initErrorCapture, errorCapture } from './debug-utils'
import './diagnostic' // Import diagnostics
import { vectorStore } from '@/lib/ai/vector-store-provider'

// Initialize our error capture system
initErrorCapture();

// Log that we're starting the app
console.log('[FlexHelp] Application starting...');

// Initialize offline support
initializeOfflineSupport().catch(error => {
  console.error('Offline support initialization failed:', error);
  errorCapture.captureError('Offline support initialization failed: ' + error.message, error.stack);
});

const root = document.getElementById('root')

if (!root) {
  const message = 'Root element not found';
  errorCapture.captureError(message);
  throw new Error(message);
}

// Add a visible marker to the page that shows we're loading
const loadingElement = document.createElement('div');
loadingElement.id = 'app-loading-indicator';
loadingElement.style.position = 'fixed';
loadingElement.style.top = '50%';
loadingElement.style.left = '50%';
loadingElement.style.transform = 'translate(-50%, -50%)';
loadingElement.style.padding = '10px 20px';
loadingElement.style.background = 'rgba(0,0,0,0.7)';
loadingElement.style.color = 'white';
loadingElement.style.borderRadius = '4px';
loadingElement.style.zIndex = '9999';
loadingElement.style.fontFamily = 'sans-serif';
loadingElement.textContent = 'Loading FlexHelp...';
document.body.appendChild(loadingElement);

try {
  console.log('[FlexHelp] Attempting to render app...');
  
  // Add diagnostic info
  if (window.__FLEXHELP_DIAGNOSTICS__) {
    window.__FLEXHELP_DIAGNOSTICS__.log('Starting app render');
  }
  
  // Check and log RAG system status at startup
  try {
    console.log('[FlexHelp] Checking RAG system status at startup');
    const isReady = vectorStore?.isReady?.() || false;
    const documentCount = vectorStore?.getDocumentCount?.() || 0;
    console.log(`[FlexHelp] RAG system status: initialized=${isReady}, documentCount=${documentCount}`);
    
    // Create a global debugging object
    window.__FLEXHELP_DEBUG = {
      ragStatus: {
        initialized: isReady,
        documentCount: documentCount,
        lastChecked: new Date().toISOString()
      },
      refreshRAGStatus: () => {
        try {
          const updatedIsReady = vectorStore?.isReady?.() || false;
          const updatedDocCount = vectorStore?.getDocumentCount?.() || 0;
          window.__FLEXHELP_DEBUG.ragStatus = {
            initialized: updatedIsReady,
            documentCount: updatedDocCount,
            lastChecked: new Date().toISOString()
          };
          console.log(`[FlexHelp] Updated RAG status: initialized=${updatedIsReady}, documentCount=${updatedDocCount}`);
        } catch (err) {
          console.error('[FlexHelp] Failed to refresh RAG status:', err);
        }
      }
    };
  } catch (error) {
    console.error('[FlexHelp] Error checking RAG system status:', error);
  }
  
  // Check if root already has a React instance attached
  const rootKey = '_reactRootContainer';
  if (!root[rootKey]) {
    const rootElement = ReactDOM.createRoot(root);
    rootElement.render(
      <React.StrictMode>
        <ThemeProvider defaultTheme="light" storageKey="flexhelp-ui-theme">
          <App />
        </ThemeProvider>
      </React.StrictMode>,
    );
  } else {
    console.log('[FlexHelp] React root already exists, skipping re-render');
  }
  
  // If rendering succeeds, we can remove the loading indicator
  setTimeout(() => {
    const loadingIndicator = document.getElementById('app-loading-indicator');
    if (loadingIndicator) {
      loadingIndicator.style.opacity = '0';
      loadingIndicator.style.transition = 'opacity 0.5s';
      setTimeout(() => {
        if (loadingIndicator.parentNode) {
          loadingIndicator.parentNode.removeChild(loadingIndicator);
        }
      }, 500);
    }
    
    // Add diagnostic info
    if (window.__FLEXHELP_DIAGNOSTICS__) {
      window.__FLEXHELP_DIAGNOSTICS__.log('App rendered successfully');
    }
  }, 1000);
  
  console.log('[FlexHelp] App rendered successfully!');
} catch (error: unknown) {
  console.error('Failed to render app:', error);
  
  // Type-safe error handling
  if (error instanceof Error) {
    // If it's a standard Error object
    errorCapture.captureError('Failed to render app: ' + error.message, error.stack);
  } else {
    // For non-standard errors
    const errorMessage = typeof error === 'string' ? error : 'Unknown error';
    errorCapture.captureError('Failed to render app: ' + errorMessage);
  }
  
  // Add diagnostic info
  if (window.__FLEXHELP_DIAGNOSTICS__) {
    // Convert unknown error to Error if needed for diagnostics
    const diagnosticError = error instanceof Error ? error : new Error(String(error));
    window.__FLEXHELP_DIAGNOSTICS__.captureError(diagnosticError, 'main.tsx:render');
  }
  
  // Update loading indicator to show error
  const loadingIndicator = document.getElementById('app-loading-indicator');
  if (loadingIndicator) {
    loadingIndicator.style.background = 'rgba(220,0,0,0.8)';
    loadingIndicator.textContent = 'Failed to load app. See console for details.';
  }
}

// Add this to make TypeScript happy with our debug global
declare global {
  interface Window {
    __FLEXHELP_DEBUG: {
      ragStatus: {
        initialized: boolean;
        documentCount: number;
        lastChecked: string;
      };
      refreshRAGStatus: () => void;
    };
  }
}